import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { OktaAuthStateService } from '@okta/okta-angular';
import { Observable } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';

import { environment } from 'src/environments/environment';
import { ApiService } from './api.service';
import { AppUuidService } from './app-uuid.service';

interface IParameter {
  [key: string]: any;
}

interface IHeader {
  [key: string]: any;
}

enum AUTH_HEADER {
  AUTH_TOKEN = 'Authorization',
  AUTH_UUID = 'x-gls-request-id',
}

@Injectable({ providedIn: 'root' })
export class SyncApiService implements ApiService {
  private apiUrl: string = environment.apiUrl;

  constructor(private http: HttpClient, public okta: OktaAuthStateService, private appUuidService: AppUuidService) {}

  get<T>(
    path: string,
    params?: any,
    headers?: any,
    responseType?: 'json' | 'arraybuffer' | 'blob' | 'text',
    observe?: 'body' | 'response',
    externalUrl?: ''
  ): Observable<T>;
  get(
    path: string,
    params: IParameter = {},
    headers: IHeader = {},
    responseType: 'json' | 'arraybuffer' | 'blob' | 'text' = 'json',
    observe?: 'body' | 'response',
    externalUrl?: ''
  ): Observable<any> {
    return this.request('get', path, params, headers, responseType, observe, externalUrl);
  }

  post<T>(
    path: string,
    params?: any,
    headers?: any,
    responseType?: 'json' | 'arraybuffer' | 'blob' | 'text',
    observe?: 'body' | 'response'
  ): Observable<T>;
  post(
    path: string,
    params: IParameter = {},
    headers: IHeader = {},
    responseType: 'json' | 'arraybuffer' | 'blob' | 'text' = 'json',
    observe?: 'body' | 'response'
  ): Observable<any> {
    return this.request('post', path, params, headers, responseType, observe);
  }

  patch(path: string, params: IParameter = {}, headers: IHeader = {}): Observable<any> {
    return this.request('patch', path, params, headers);
  }

  put<T>(path: string, params: {}, headers?: {}): Observable<T>;
  put(path: string, params: IParameter = {}, headers: IHeader = {}): Observable<any> {
    return this.request('put', path, params, headers);
  }

  delete(path: string, params: IParameter = {}, headers: IHeader = {}): Observable<any> {
    return this.request('delete', path, params, headers);
  }

  request(
    method: string,
    path: string,
    params: IParameter = {},
    headers: IHeader = {},
    responseType: 'json' | 'arraybuffer' | 'blob' | 'text' = 'json',
    observe: 'body' | 'response' = 'body',
    externalUrl?: ''
  ): Observable<any> {
    return this.getAuthorizationHeaders().pipe(
      switchMap(authHeaders => {
        const httpHeaders = new HttpHeaders({
          ...headers,
          ...authHeaders,
        });
        const hasBody = ['post', 'patch', 'put', 'delete'].includes(method);
        const requestPath = externalUrl ? path : `${this.apiUrl}${path}`;

        return this.http.request(method, requestPath, {
          headers: httpHeaders,
          body: hasBody ? params : {},
          observe,
          params: !hasBody ? params : {},
          responseType,
        });
      })
    );
  }

  postFormData<T>(path: string, data: FormData, queryParams?: any, headers?: any, responseType?: any): Observable<T>;
  postFormData(
    path: string,
    data: FormData,
    queryParams: IParameter = {},
    headers: IHeader = {},
    responseType: any
  ): Observable<any> {
    return this.getAuthorizationHeaders().pipe(
      switchMap(authHeaders => {
        const httpHeaders = new HttpHeaders({
          ...headers,
          ...authHeaders,
        });

        return this.http.post(`${this.apiUrl}${path}`, data, {
          headers: httpHeaders,
          params: queryParams,
          responseType,
        });
      })
    );
  }

  putFormData<T>(path: string, data: FormData, headers?: any): Observable<T>;
  putFormData(path: string, data: FormData, headers: IHeader = {}): Observable<any> {
    return this.getAuthorizationHeaders().pipe(
      switchMap(authHeaders => {
        const httpHeaders = new HttpHeaders({
          ...headers,
          ...authHeaders,
        });

        return this.http.put(`${this.apiUrl}${path}`, data, {
          headers: httpHeaders,
        });
      })
    );
  }

  getAuthorizationHeaders = (): Observable<any> => {
    const { okta, appUuidService } = this;

    return okta.authState$.pipe(
      filter(({ isAuthenticated }) => isAuthenticated),
      take(1),
      map(({ accessToken }) => {
        return {
          [AUTH_HEADER.AUTH_TOKEN]: `Bearer ${accessToken.accessToken}`,
          [AUTH_HEADER.AUTH_UUID]: appUuidService.appUuid,
        };
      })
    );
  };
}
